import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ChartBar, Notepad, Receipt, Sparkle, Plus, Trash, FloppyDisk,
  FilePdf, FileXls, ArrowsClockwise, Star, Horse, PuzzlePiece, Dog,
  TrendUp, TrendDown, Upload, Calendar, ChartPie, Lightning, Target,
} from "@phosphor-icons/react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { api, API, fmtINR } from "../lib/kostra";
import { useAuth } from "../lib/auth";

const TOKEN_KEY = "kostra:token";

const CLASS_META = {
  star:       { icon: Star,        label: "Star",       cls: "bg-emerald-50 border-emerald-500 text-emerald-700" },
  plowhorse:  { icon: Horse,       label: "Plowhorse",  cls: "bg-amber-50 border-amber-500 text-amber-700" },
  puzzle:     { icon: PuzzlePiece, label: "Puzzle",     cls: "bg-blue-50 border-blue-500 text-blue-700" },
  dog:        { icon: Dog,         label: "Dog",        cls: "bg-rose-50 border-rose-500 text-rose-700" },
  unclassified:{ icon: Sparkle,    label: "No data",    cls: "bg-[#F9FAFB] border-[#E5E7EB] text-[#4B5563]" },
};

export default function MenuEngineeringPage() {
  const { isAdmin, openLogin } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [items, setItems] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [recos, setRecos] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", platform: "all" });
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filters.date_from) q.set("date_from", filters.date_from);
      if (filters.date_to) q.set("date_to", filters.date_to);
      if (filters.platform && filters.platform !== "all") q.set("platform", filters.platform);
      const [dash, it, mat, rec, rcp] = await Promise.all([
        api.get(`/menu-engineering/dashboard?${q}`),
        api.get(`/menu-engineering/items`),
        api.get(`/menu-engineering/matrix?${q}`),
        api.get(`/menu-engineering/recommendations?${q}`),
        api.get(`/recipes`),
      ]);
      setDashboard(dash.data);
      setItems(it.data);
      setMatrix(mat.data);
      setRecos(rec.data);
      setRecipes(rcp.data);
    } catch { toast.error("Failed to load engineering data"); }
    finally { setLoading(false); }
  }, [isAdmin, filters]);
  useEffect(() => { refresh(); }, [refresh]);

  const seedFromMenus = async () => {
    try {
      const { data } = await api.post("/menu-engineering/items/seed-from-menus");
      toast.success(`Seeded ${data.seeded} items from your menus`);
      refresh();
    } catch { toast.error("Seed failed"); }
  };

  const downloadExport = async (fmt) => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const q = new URLSearchParams();
      if (filters.date_from) q.set("date_from", filters.date_from);
      if (filters.date_to) q.set("date_to", filters.date_to);
      if (filters.platform && filters.platform !== "all") q.set("platform", filters.platform);
      const resp = await fetch(`${API}/menu-engineering/export/${fmt}?${q}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `KOSTRA_Menu_Engineering.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch { toast.error("Export failed"); }
  };

  if (!isAdmin) {
    return (
      <div className="app-shell">
        <Header />
        <div className="bg-gradient-to-b from-[#0A3B24] to-[#0E5232] text-white">
          <div className="max-w-7xl mx-auto px-5 md:px-8 py-14 text-center">
            <div className="text-[#D4AF37] text-xs tracking-[0.25em] uppercase font-semibold inline-flex items-center gap-2 justify-center">
              <ChartBar size={14} weight="fill" /> Menu Engineering
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-medium mt-3 leading-tight">
              Profit-engineered <br className="hidden md:block" />
              <span className="text-[#D4AF37]">menus.</span>
            </h1>
            <p className="text-white/75 text-sm md:text-base mt-3 max-w-2xl mx-auto">
              Classify every dish (Star / Plowhorse / Puzzle / Dog), and act on
              data-driven recommendations to lift margin and revenue.
            </p>
            <Button onClick={() => openLogin()} className="mt-6 bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold h-11 px-6"
                    data-testid="engineering-guest-login-btn">
              Admin login to engineer your menu
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="app-shell">
      <Header />

      <div className="bg-gradient-to-b from-[#0A3B24] to-[#0E5232] text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-10 pb-12">
          <div className="text-[#D4AF37] text-xs tracking-[0.25em] uppercase font-semibold inline-flex items-center gap-2">
            <ChartBar size={14} weight="fill" /> Menu Engineering · Profit Matrix
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-medium mt-2 leading-tight">
            Engineer your menu — <span className="text-[#D4AF37]">promote stars, rescue plowhorses, retire dogs.</span>
          </h1>
          <p className="text-white/70 text-sm mt-2 max-w-3xl">
            Live food cost from Recipe Library &amp; Ingredients Master · auto Star/Plowhorse/Puzzle/Dog
            classification · rule-based recommendation engine · PDF &amp; Excel exports.
          </p>

          {/* Filters */}
          <div className="mt-5 flex flex-wrap gap-2 items-end">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-white/60 font-bold mb-1 block">From</Label>
              <Input type="date" value={filters.date_from}
                     onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                     className="h-9 bg-white/10 border-white/20 text-white text-sm w-40"
                     data-testid="eng-date-from" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-white/60 font-bold mb-1 block">To</Label>
              <Input type="date" value={filters.date_to}
                     onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                     className="h-9 bg-white/10 border-white/20 text-white text-sm w-40"
                     data-testid="eng-date-to" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-white/60 font-bold mb-1 block">Platform</Label>
              <Select value={filters.platform}
                      onValueChange={(v) => setFilters({ ...filters, platform: v })}>
                <SelectTrigger className="h-9 w-36 bg-white/10 border-white/20 text-white text-sm" data-testid="eng-platform"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All platforms</SelectItem>
                  <SelectItem value="dine_in">Dine-in</SelectItem>
                  <SelectItem value="swiggy">Swiggy</SelectItem>
                  <SelectItem value="zomato">Zomato</SelectItem>
                  <SelectItem value="direct">Direct</SelectItem>
                  <SelectItem value="ondc">ONDC</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={refresh} variant="outline" size="sm"
                    className="bg-white/10 border-white/20 text-white hover:bg-white/20 font-semibold h-9"
                    data-testid="eng-refresh">
              <ArrowsClockwise size={12} weight="bold" className="mr-1" /> Refresh
            </Button>
            <div className="ml-auto flex gap-2">
              <Button onClick={() => downloadExport("pdf")} variant="outline" size="sm"
                      className="bg-white/10 border-white/20 text-white hover:bg-white/20 font-semibold h-9"
                      data-testid="eng-export-pdf">
                <FilePdf size={12} weight="bold" className="mr-1" /> PDF
              </Button>
              <Button onClick={() => downloadExport("excel")} size="sm"
                      className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold h-9"
                      data-testid="eng-export-excel">
                <FileXls size={12} weight="bold" className="mr-1" /> Excel
              </Button>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-5 md:px-8 -mt-6 pb-14 space-y-5">
        {/* Sub-tabs */}
        <div className="kostra-card p-1.5 inline-flex gap-1 self-start flex-wrap" data-testid="eng-tabs">
          {[
            { key: "dashboard", label: "Dashboard", icon: <ChartBar size={14} weight="bold" /> },
            { key: "items", label: "Menu Items", icon: <Notepad size={14} weight="bold" /> },
            { key: "sales", label: "Sales Data", icon: <Receipt size={14} weight="bold" /> },
            { key: "matrix", label: "Engineering Matrix", icon: <Target size={14} weight="bold" /> },
            { key: "recos", label: "Recommendations", icon: <Lightning size={14} weight="bold" /> },
          ].map((t) => (
            <button key={t.key} type="button" onClick={() => setActiveTab(t.key)}
                    className={`text-xs px-3 py-2 rounded-md font-semibold inline-flex items-center gap-1.5 ${
                      activeTab === t.key ? "bg-[#0A3B24] text-white" : "text-[#0A3B24] hover:bg-[#FDF9EE]"}`}
                    data-testid={`eng-tab-${t.key}`}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {items.length === 0 && (
          <div className="kostra-card p-8 text-center" data-testid="eng-empty">
            <Sparkle size={28} weight="duotone" className="inline mb-2 text-[#D4AF37]" />
            <div className="font-display text-lg text-[#0A3B24]">No menu items yet</div>
            <div className="text-xs text-[#4B5563] mt-1 max-w-md mx-auto">
              Start by seeding from your saved menus, or add items manually under
              the <strong>Menu Items</strong> tab.
            </div>
            <Button onClick={seedFromMenus} className="mt-4 bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold"
                    data-testid="eng-seed">
              <Sparkle size={13} weight="fill" className="mr-1.5" /> Seed from my menus
            </Button>
          </div>
        )}

        {activeTab === "dashboard" && dashboard && <DashboardTab dashboard={dashboard} />}
        {activeTab === "items"     && <ItemsTab items={items} recipes={recipes} onChange={refresh} />}
        {activeTab === "sales"     && <SalesTab items={items} onChange={refresh} />}
        {activeTab === "matrix"    && <MatrixTab matrix={matrix} />}
        {activeTab === "recos"     && <RecosTab recos={recos} />}
      </main>

      <Footer />
    </div>
  );
}

/* ----------------------------------------------------------------------
 * Dashboard
 * -------------------------------------------------------------------- */
function DashboardTab({ dashboard }) {
  const k = dashboard.kpis;
  const KPIs = [
    { label: "Menu Items",         value: k.total_menu_items, accent: "" },
    { label: "Avg Food Cost %",    value: `${k.avg_food_cost_pct.toFixed(1)}%`, accent: k.avg_food_cost_pct > 35 ? "warn" : "ok" },
    { label: "Avg Gross Margin %", value: `${k.avg_gross_margin_pct.toFixed(1)}%`, accent: "gold" },
    { label: "Total Revenue",      value: fmtINR(k.total_menu_revenue), accent: "gold" },
    { label: "Stars",              value: k.star_count, accent: "ok" },
    { label: "Plowhorses",         value: k.plowhorse_count, accent: "warn" },
    { label: "Puzzles",            value: k.puzzle_count, accent: "" },
    { label: "Dogs",               value: k.dog_count, accent: "danger" },
    { label: "Potential ↑ Profit", value: fmtINR(k.potential_profit_improvement), accent: "gold" },
    { label: "Suggested Repricings", value: k.recommended_price_updates, accent: "" },
  ];
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="eng-kpi-grid">
        {KPIs.map((kpi) => <KPICard key={kpi.label} {...kpi} />)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ChartCard title="Revenue by Category" data={dashboard.revenue_by_category}
                   xKey="category" yKey="revenue" testid="chart-rev-cat" />
        <ChartCard title="Avg Food Cost % by Category" data={dashboard.foodcost_by_category}
                   xKey="category" yKey="avg_food_cost_pct" suffix="%" testid="chart-fc-cat" color="#A87C0E" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <RankedList title="Top Selling" icon={<TrendUp size={14} weight="bold" />}
                    items={dashboard.top_selling} testid="top-selling" />
        <RankedList title="Bottom Performing" icon={<TrendDown size={14} weight="bold" />}
                    items={dashboard.bottom_performing} testid="bottom-performing" />
      </div>
    </div>
  );
}

function KPICard({ label, value, accent }) {
  const cls = accent === "gold" ? "text-[#A87C0E]"
    : accent === "ok" ? "text-emerald-700"
    : accent === "warn" ? "text-amber-700"
    : accent === "danger" ? "text-rose-700" : "text-[#0A3B24]";
  return (
    <div className="kostra-card p-3">
      <div className="text-[10px] uppercase tracking-wider text-[#4B5563] font-bold">{label}</div>
      <div className={`mt-1 font-mono-num font-bold text-xl ${cls}`}>{value}</div>
    </div>
  );
}

function ChartCard({ title, data, xKey, yKey, suffix = "", color = "#0A3B24", testid }) {
  const max = Math.max(1, ...(data || []).map((d) => Number(d[yKey] || 0)));
  return (
    <div className="kostra-card p-5" data-testid={testid}>
      <h3 className="kostra-section-title text-base mb-3 inline-flex items-center gap-2">
        <ChartPie size={14} weight="duotone" className="text-[#D4AF37]" /> {title}
      </h3>
      {(!data || data.length === 0) && (
        <div className="text-xs text-[#4B5563] py-6 text-center">No data for this filter</div>
      )}
      <div className="space-y-2">
        {(data || []).slice(0, 8).map((d, i) => {
          const v = Number(d[yKey] || 0);
          const pct = (v / max) * 100;
          return (
            <div key={i} className="text-sm">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[#0A3B24] truncate pr-2">{d[xKey] || "—"}</span>
                <span className="font-mono-num font-bold text-[#0A3B24]">
                  {suffix === "%" ? `${v.toFixed(1)}%` : fmtINR(v)}
                </span>
              </div>
              <div className="h-2 rounded-full bg-[#F9FAFB] overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RankedList({ title, icon, items, testid }) {
  return (
    <div className="kostra-card p-5" data-testid={testid}>
      <h3 className="kostra-section-title text-base mb-3 inline-flex items-center gap-2">
        <span className="text-[#D4AF37]">{icon}</span> {title}
      </h3>
      {(!items || items.length === 0) && (
        <div className="text-xs text-[#4B5563] py-6 text-center">No data</div>
      )}
      {(items || []).map((i, idx) => {
        const c = CLASS_META[i.classification] || CLASS_META.unclassified;
        const Icon = c.icon;
        return (
          <div key={i.id} className="flex items-center gap-3 py-2 border-b border-[#E5E7EB] last:border-0">
            <span className="font-mono-num text-xs text-[#4B5563] w-6 text-right">#{idx + 1}</span>
            <div className="flex-1 min-w-0">
              <div className="font-display text-sm text-[#0A3B24] truncate">{i.name}</div>
              <div className="text-[11px] text-[#4B5563]">
                {i.category} · Qty: <span className="font-mono-num">{i.quantity_sold}</span> · Rev: <span className="font-mono-num">{fmtINR(i.revenue)}</span>
              </div>
            </div>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border-2 font-bold inline-flex items-center gap-1 ${c.cls}`}>
              <Icon size={10} weight="bold" /> {c.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ----------------------------------------------------------------------
 * Items CRUD
 * -------------------------------------------------------------------- */
function blankItem() {
  return { name: "", category: "", sub_category: "", sku: "", selling_price: 0,
           delivery_price: 0, dine_in_price: 0, packaging_cost: 0,
           recipe_id: "", food_cost: 0, status: "active", brand: "", outlet: "" };
}

function ItemsTab({ items, recipes, onChange }) {
  const [draft, setDraft] = useState(blankItem());
  const [editingId, setEditingId] = useState(null);
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return items;
    return items.filter((i) =>
      `${i.name} ${i.category} ${i.sub_category} ${i.sku}`.toLowerCase().includes(n));
  }, [items, q]);

  const save = async () => {
    if (!draft.name.trim()) { toast.error("Item name required"); return; }
    try {
      const payload = { ...draft,
        selling_price: Number(draft.selling_price || 0),
        delivery_price: Number(draft.delivery_price || 0),
        dine_in_price: Number(draft.dine_in_price || 0),
        packaging_cost: Number(draft.packaging_cost || 0),
        food_cost: Number(draft.food_cost || 0),
        recipe_id: draft.recipe_id || null };
      if (editingId) {
        await api.put(`/menu-engineering/items/${editingId}`, payload);
        toast.success("Item updated");
      } else {
        await api.post(`/menu-engineering/items`, payload);
        toast.success("Item added");
      }
      setDraft(blankItem()); setEditingId(null); onChange();
    } catch { toast.error("Save failed"); }
  };

  const edit = (it) => {
    setEditingId(it.id);
    setDraft({ ...blankItem(), ...it, recipe_id: it.recipe_id || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (it) => {
    if (!window.confirm(`Delete "${it.name}"? Sales data for this item will also be removed.`)) return;
    try { await api.delete(`/menu-engineering/items/${it.id}`); toast.success("Deleted"); onChange(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="kostra-card p-5">
        <h2 className="kostra-section-title text-base mb-3 inline-flex items-center gap-2">
          <Plus size={14} weight="duotone" className="text-[#D4AF37]" />
          {editingId ? "Edit Menu Item" : "Add Menu Item"}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-12 gap-3">
          <Field label="Name *"      span={4} value={draft.name}      onChange={(v) => setDraft({ ...draft, name: v })}      testid="eng-item-name" />
          <Field label="Category"    span={2} value={draft.category}  onChange={(v) => setDraft({ ...draft, category: v })}  testid="eng-item-category" />
          <Field label="Sub-Cat"     span={2} value={draft.sub_category} onChange={(v) => setDraft({ ...draft, sub_category: v })} />
          <Field label="SKU"         span={2} value={draft.sku}        onChange={(v) => setDraft({ ...draft, sku: v })} />
          <div className="col-span-2 md:col-span-2">
            <Label className="kostra-data-label mb-1 block">Status</Label>
            <Select value={draft.status} onValueChange={(v) => setDraft({ ...draft, status: v })}>
              <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Field label="Selling Price (₹) *" span={2} type="number" value={draft.selling_price}  onChange={(v) => setDraft({ ...draft, selling_price: v })}  testid="eng-item-sp" />
          <Field label="Delivery Price (₹)" span={2} type="number" value={draft.delivery_price} onChange={(v) => setDraft({ ...draft, delivery_price: v })} />
          <Field label="Dine-in Price (₹)"  span={2} type="number" value={draft.dine_in_price}  onChange={(v) => setDraft({ ...draft, dine_in_price: v })} />
          <Field label="Packaging (₹)"      span={2} type="number" value={draft.packaging_cost} onChange={(v) => setDraft({ ...draft, packaging_cost: v })} />
          <Field label="Food Cost (₹)"      span={2} type="number" value={draft.food_cost}      onChange={(v) => setDraft({ ...draft, food_cost: v })} />
          <div className="col-span-2 md:col-span-2">
            <Label className="kostra-data-label mb-1 block">Link to Recipe</Label>
            <Select value={draft.recipe_id || "_none"} onValueChange={(v) => setDraft({ ...draft, recipe_id: v === "_none" ? "" : v })}>
              <SelectTrigger className="h-10"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="_none">None (manual food cost)</SelectItem>
                {recipes.map((r) => <SelectItem key={r.id} value={r.id}>{r.dish_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 md:col-span-12 flex justify-end gap-2 pt-2">
            {editingId && (
              <Button variant="outline" onClick={() => { setEditingId(null); setDraft(blankItem()); }}
                      className="border-[#0A3B24]/30 text-[#0A3B24] hover:bg-[#FDF9EE] font-semibold">Cancel</Button>
            )}
            <Button onClick={save} className="bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold" data-testid="eng-item-save">
              <FloppyDisk size={13} weight="bold" className="mr-1" />
              {editingId ? "Update" : "Add Item"}
            </Button>
          </div>
        </div>
      </div>

      <div className="kostra-card p-0 overflow-hidden">
        <div className="px-5 py-3 flex items-center justify-between border-b border-[#E5E7EB]">
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search by name / category / SKU…"
                 className="h-9 w-72 text-sm" data-testid="eng-item-search" />
          <div className="text-xs text-[#4B5563]">{filtered.length} item{filtered.length === 1 ? "" : "s"}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="eng-items-table">
            <thead className="bg-[#F9FAFB] text-[10px] uppercase tracking-wider text-[#4B5563]">
              <tr>
                <th className="text-left px-4 py-2.5 font-bold">Item</th>
                <th className="text-left px-3 py-2.5 font-bold">Category</th>
                <th className="text-right px-3 py-2.5 font-bold">Selling</th>
                <th className="text-right px-3 py-2.5 font-bold">Food Cost</th>
                <th className="text-right px-3 py-2.5 font-bold">FC%</th>
                <th className="text-right px-3 py-2.5 font-bold">GM%</th>
                <th className="text-center px-3 py-2.5 font-bold">Status</th>
                <th className="text-right px-4 py-2.5 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-[#4B5563]">No items.</td></tr>
              )}
              {filtered.map((it) => (
                <tr key={it.id} className="border-t border-[#E5E7EB] hover:bg-[#FDF9EE]/40"
                    data-testid={`eng-item-row-${it.name.replace(/\s+/g, "-").toLowerCase()}`}>
                  <td className="px-4 py-2.5 font-display text-[#0A3B24]">{it.name}</td>
                  <td className="px-3 py-2.5 text-[#4B5563]">{it.category}</td>
                  <td className="px-3 py-2.5 text-right font-mono-num">{fmtINR(it.selling_price)}</td>
                  <td className="px-3 py-2.5 text-right font-mono-num text-[#4B5563]">{fmtINR(it.food_cost)}</td>
                  <td className="px-3 py-2.5 text-right font-mono-num">{(it.food_cost_pct || 0).toFixed(1)}%</td>
                  <td className="px-3 py-2.5 text-right font-mono-num font-bold text-emerald-700">{(it.gross_margin_pct || 0).toFixed(1)}%</td>
                  <td className="px-3 py-2.5 text-center">
                    <Badge variant="outline" className={it.status === "active"
                      ? "border-emerald-500 text-emerald-700 bg-emerald-50"
                      : "border-[#E5E7EB] text-[#4B5563]"}>
                      {it.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => edit(it)}
                              className="h-8 px-2 rounded-md text-xs font-semibold text-[#0A3B24] hover:bg-[#FDF9EE]"
                              data-testid={`eng-item-edit-${it.id}`}>Edit</button>
                      <button onClick={() => del(it)}
                              className="h-8 px-2 rounded-md text-[#DC2626] hover:bg-[#FBE8E8]"
                              data-testid={`eng-item-delete-${it.id}`}><Trash size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Field({ label, span = 4, value, onChange, type = "text", testid }) {
  return (
    <div className={`col-span-2 md:col-span-${span}`}>
      <Label className="kostra-data-label mb-1 block">{label}</Label>
      <Input type={type} value={value ?? ""}
             onChange={(e) => onChange(type === "number" ? parseFloat(e.target.value || 0) : e.target.value)}
             className={`h-10 ${type === "number" ? "font-mono-num" : ""}`}
             data-testid={testid} />
    </div>
  );
}

/* ----------------------------------------------------------------------
 * Sales — manual entry + CSV import
 * -------------------------------------------------------------------- */
function SalesTab({ items, onChange }) {
  const [entry, setEntry] = useState({ item_id: "", platform: "direct", date: "", quantity_sold: 0, revenue: 0 });
  const [uploading, setUploading] = useState(false);

  const submitOne = async () => {
    if (!entry.item_id) { toast.error("Pick an item"); return; }
    if (!entry.quantity_sold && !entry.revenue) { toast.error("Quantity or revenue required"); return; }
    try {
      await api.post(`/menu-engineering/sales`, { entries: [entry] });
      toast.success("Sales entry added");
      setEntry({ ...entry, quantity_sold: 0, revenue: 0 });
      onChange();
    } catch { toast.error("Failed to add"); }
  };

  const onCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem(TOKEN_KEY);
      const resp = await fetch(`${API}/menu-engineering/sales/import-csv`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!resp.ok) throw new Error("Import failed");
      const data = await resp.json();
      toast.success(`Imported ${data.inserted} rows${data.skipped?.length ? ` · ${data.skipped.length} skipped` : ""}`);
      onChange();
    } catch (err) { toast.error(err.message || "Import failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <div className="space-y-4">
      <div className="kostra-card p-5">
        <h2 className="kostra-section-title text-base mb-3 inline-flex items-center gap-2">
          <Receipt size={14} weight="duotone" className="text-[#D4AF37]" /> Add Sales Entry
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-12 gap-3">
          <div className="col-span-2 md:col-span-4">
            <Label className="kostra-data-label mb-1 block">Item *</Label>
            <Select value={entry.item_id} onValueChange={(v) => setEntry({ ...entry, item_id: v })}>
              <SelectTrigger className="h-10" data-testid="eng-sales-item"><SelectValue placeholder="Pick item" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {items.map((it) => <SelectItem key={it.id} value={it.id}>{it.name}{it.category ? ` · ${it.category}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 md:col-span-2">
            <Label className="kostra-data-label mb-1 block">Platform</Label>
            <Select value={entry.platform} onValueChange={(v) => setEntry({ ...entry, platform: v })}>
              <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="direct">Direct</SelectItem>
                <SelectItem value="swiggy">Swiggy</SelectItem>
                <SelectItem value="zomato">Zomato</SelectItem>
                <SelectItem value="dine_in">Dine-in</SelectItem>
                <SelectItem value="ondc">ONDC</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Field label="Date" type="date" span={2} value={entry.date} onChange={(v) => setEntry({ ...entry, date: v })} />
          <Field label="Quantity *" type="number" span={2} value={entry.quantity_sold} onChange={(v) => setEntry({ ...entry, quantity_sold: v })} testid="eng-sales-qty" />
          <Field label="Revenue (₹) *" type="number" span={2} value={entry.revenue} onChange={(v) => setEntry({ ...entry, revenue: v })} testid="eng-sales-rev" />
          <div className="col-span-2 md:col-span-12 flex justify-end pt-2">
            <Button onClick={submitOne} className="bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold" data-testid="eng-sales-add">
              <Plus size={13} weight="bold" className="mr-1" /> Add Sales
            </Button>
          </div>
        </div>
      </div>

      <div className="kostra-card p-5">
        <h2 className="kostra-section-title text-base mb-2 inline-flex items-center gap-2">
          <Upload size={14} weight="duotone" className="text-[#D4AF37]" /> Bulk Import via CSV
        </h2>
        <p className="text-xs text-[#4B5563] mb-3 max-w-2xl">
          CSV columns: <code className="bg-[#F9FAFB] px-1.5 py-0.5 rounded text-[#0A3B24] font-mono">item_name, quantity_sold, revenue, platform, date</code>.
          Item names must match existing items (case-insensitive). Optional: platform, date.
        </p>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold text-sm">
            <Upload size={13} weight="bold" /> {uploading ? "Uploading…" : "Choose CSV"}
          </span>
          <input type="file" accept=".csv" onChange={onCsv} className="hidden" disabled={uploading} data-testid="eng-sales-csv" />
        </label>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------
 * Engineering Matrix
 * -------------------------------------------------------------------- */
function MatrixTab({ matrix }) {
  if (!matrix || matrix.points.length === 0) {
    return <div className="kostra-card p-10 text-center text-[#4B5563]">No data yet. Add items + sales to see the matrix.</div>;
  }
  const xMax = Math.max(1, ...matrix.points.map((p) => p.x));
  const yMax = Math.max(10, ...matrix.points.map((p) => p.y));
  const xMed = matrix.qty_median;
  const yMed = matrix.margin_median;
  // Map a point to %
  const px = (v) => `${Math.min(98, (v / xMax) * 96 + 2)}%`;
  const py = (v) => `${Math.min(95, 96 - (v / yMax) * 92)}%`;
  return (
    <div className="space-y-4" data-testid="eng-matrix">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Quadrant label="Star" sub="High Pop · High Profit" icon={Star} cls={CLASS_META.star.cls}
                  count={matrix.points.filter((p) => p.classification === "star").length} />
        <Quadrant label="Plowhorse" sub="High Pop · Low Profit" icon={Horse} cls={CLASS_META.plowhorse.cls}
                  count={matrix.points.filter((p) => p.classification === "plowhorse").length} />
        <Quadrant label="Puzzle" sub="Low Pop · High Profit" icon={PuzzlePiece} cls={CLASS_META.puzzle.cls}
                  count={matrix.points.filter((p) => p.classification === "puzzle").length} />
        <Quadrant label="Dog" sub="Low Pop · Low Profit" icon={Dog} cls={CLASS_META.dog.cls}
                  count={matrix.points.filter((p) => p.classification === "dog").length} />
      </div>

      <div className="kostra-card p-5">
        <h2 className="kostra-section-title text-base mb-3">2 × 2 Profit Matrix</h2>
        <div className="relative h-[480px] bg-[#FDF9EE]/30 rounded-lg border border-[#E5E7EB] overflow-hidden">
          {/* Median axes */}
          <div className="absolute left-0 right-0 border-t-2 border-dashed border-[#D4AF37]"
               style={{ top: py(yMed) }}>
            <span className="absolute -top-4 right-2 text-[10px] uppercase tracking-wider text-[#A87C0E] font-bold">
              Median GM% {yMed.toFixed(1)}%
            </span>
          </div>
          <div className="absolute top-0 bottom-0 border-l-2 border-dashed border-[#D4AF37]"
               style={{ left: px(xMed) }}>
            <span className="absolute bottom-2 left-2 text-[10px] uppercase tracking-wider text-[#A87C0E] font-bold">
              Median Qty {xMed}
            </span>
          </div>
          {/* Quadrant labels */}
          <span className="absolute top-2 right-3 text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-500 px-2 py-0.5 rounded-full">
            ★ Star
          </span>
          <span className="absolute top-2 left-3 text-[10px] uppercase tracking-wider font-bold text-blue-700 bg-blue-50 border border-blue-500 px-2 py-0.5 rounded-full">
            ⊕ Puzzle
          </span>
          <span className="absolute bottom-2 right-3 text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-50 border border-amber-500 px-2 py-0.5 rounded-full">
            ⊙ Plowhorse
          </span>
          <span className="absolute bottom-2 left-3 text-[10px] uppercase tracking-wider font-bold text-rose-700 bg-rose-50 border border-rose-500 px-2 py-0.5 rounded-full">
            ✖ Dog
          </span>
          {/* Points */}
          {matrix.points.filter((p) => p.classification !== "unclassified").map((p) => {
            const c = CLASS_META[p.classification] || CLASS_META.unclassified;
            const r = Math.max(8, Math.min(24, Math.sqrt(p.revenue + 1) * 0.6));
            return (
              <div key={p.id}
                   className={`absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-2 shadow-sm cursor-help ${c.cls.replace('bg-', 'bg-')} `}
                   style={{ left: px(p.x), top: py(p.y), width: r, height: r }}
                   title={`${p.name}\nQty: ${p.x}, GM%: ${p.y.toFixed(1)}%\nRevenue: ₹${p.revenue.toFixed(0)}`}
                   data-testid={`matrix-point-${p.classification}`} />
            );
          })}
        </div>
        <p className="text-[11px] text-[#4B5563] mt-3">
          X-axis: quantity sold · Y-axis: gross margin % · bubble size ∝ revenue.
          Dashed gold lines are the median split.
        </p>
      </div>

      <div className="kostra-card p-0 overflow-hidden">
        <div className="px-5 py-3 border-b border-[#E5E7EB]">
          <h3 className="kostra-section-title text-base">All Classified Items</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] text-[10px] uppercase tracking-wider text-[#4B5563]">
              <tr>
                <th className="text-left px-4 py-2 font-bold">Item</th>
                <th className="text-left px-3 py-2 font-bold">Category</th>
                <th className="text-right px-3 py-2 font-bold">Qty</th>
                <th className="text-right px-3 py-2 font-bold">GM %</th>
                <th className="text-right px-3 py-2 font-bold">Revenue</th>
                <th className="text-center px-3 py-2 font-bold">Class</th>
              </tr>
            </thead>
            <tbody>
              {matrix.points.map((p) => {
                const c = CLASS_META[p.classification] || CLASS_META.unclassified;
                const Icon = c.icon;
                return (
                  <tr key={p.id} className="border-t border-[#E5E7EB]">
                    <td className="px-4 py-2 font-display text-[#0A3B24]">{p.name}</td>
                    <td className="px-3 py-2 text-[#4B5563]">{p.category}</td>
                    <td className="px-3 py-2 text-right font-mono-num">{p.x}</td>
                    <td className="px-3 py-2 text-right font-mono-num">{p.y.toFixed(1)}%</td>
                    <td className="px-3 py-2 text-right font-mono-num">{fmtINR(p.revenue)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border-2 font-bold inline-flex items-center gap-1 ${c.cls}`}>
                        <Icon size={10} weight="bold" /> {c.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Quadrant({ label, sub, icon: Icon, cls, count }) {
  return (
    <div className={`rounded-xl border-2 p-3 ${cls}`}>
      <div className="flex items-center justify-between">
        <Icon size={18} weight="duotone" />
        <span className="text-2xl font-display font-bold">{count}</span>
      </div>
      <div className="mt-1 font-display font-semibold">{label}</div>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{sub}</div>
    </div>
  );
}

/* ----------------------------------------------------------------------
 * Recommendations
 * -------------------------------------------------------------------- */
function RecosTab({ recos }) {
  if (!recos) return null;
  const groupOrder = ["Promote", "Reprice", "Reengineer", "Remove", "Introduce sales tracking"];
  const meta = {
    Promote:     { color: "border-emerald-500 bg-emerald-50 text-emerald-700", icon: Star },
    Reprice:     { color: "border-amber-500 bg-amber-50 text-amber-700",       icon: Horse },
    Reengineer:  { color: "border-blue-500 bg-blue-50 text-blue-700",          icon: PuzzlePiece },
    Remove:      { color: "border-rose-500 bg-rose-50 text-rose-700",          icon: Dog },
    "Introduce sales tracking": { color: "border-[#E5E7EB] bg-[#F9FAFB] text-[#4B5563]", icon: Sparkle },
  };
  return (
    <div className="space-y-5" data-testid="eng-recommendations">
      {groupOrder.map((g) => {
        const list = recos.groups[g] || [];
        if (list.length === 0) return null;
        const M = meta[g];
        const Icon = M.icon;
        return (
          <div key={g} className="kostra-card p-5">
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border-2 font-bold text-xs uppercase tracking-wider ${M.color}`}>
              <Icon size={12} weight="bold" /> {g} ({list.length})
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              {list.map((r) => (
                <div key={r.id} className="rounded-xl border-2 border-[#E5E7EB] bg-white p-4"
                     data-testid={`reco-${g.toLowerCase().replace(/\s+/g, "-")}-${r.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-display text-[#0A3B24] truncate">{r.name}</div>
                      <div className="text-[11px] text-[#4B5563]">{r.category}</div>
                    </div>
                    {r.suggested_price && r.suggested_price !== r.selling_price && (
                      <div className="text-right shrink-0">
                        <div className="text-[10px] uppercase tracking-wider text-[#4B5563] font-bold">Suggested</div>
                        <div className="font-mono-num font-bold text-[#A87C0E]">{fmtINR(r.suggested_price)}</div>
                        <div className="text-[10px] text-[#4B5563] line-through">{fmtINR(r.selling_price)}</div>
                      </div>
                    )}
                  </div>
                  <div className="mt-2 text-xs text-[#4B5563] leading-relaxed">{r.rationale}</div>
                  <div className="mt-2 flex flex-wrap gap-2 text-[10px]">
                    <span className="px-2 py-0.5 rounded-full bg-[#F9FAFB] border border-[#E5E7EB]">
                      Qty <span className="font-mono-num font-bold text-[#0A3B24]">{r.quantity_sold}</span>
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-[#F9FAFB] border border-[#E5E7EB]">
                      Rev <span className="font-mono-num font-bold text-[#0A3B24]">{fmtINR(r.revenue)}</span>
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-[#F9FAFB] border border-[#E5E7EB]">
                      GM <span className="font-mono-num font-bold text-emerald-700">{(r.gross_margin_pct || 0).toFixed(1)}%</span>
                    </span>
                    {r.expected_revenue_lift_pct > 0 && (
                      <span className="px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-500 text-emerald-700 font-bold">
                        +{r.expected_revenue_lift_pct}% projected
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {Object.values(recos.groups).every((g) => g.length === 0) && (
        <div className="kostra-card p-10 text-center text-[#4B5563]">
          No recommendations yet — add items &amp; sales first.
        </div>
      )}
    </div>
  );
}
